/**
 * The one entity-agnostic lookup query boundary (#2017).
 *
 * Target tables and filterable fields come from the metamodel/semantic layer.
 * Reads stay server-side under the caller's RLS, are capped as a picker, and
 * share TanStack Query's cache with every form that asks the same question.
 * Unknown targets return no rows; a filter that cannot be translated faithfully
 * refuses instead of quietly changing meaning.
 */
import type { QueryClient } from '@tanstack/react-query'
import {
  filtersToPostgrest,
  isSubjectEntity,
  isSubjectReadUnsupported,
  leavesToSubjectFilters,
  type FilterLeaf,
  type Row,
  type SchemaDescription,
  type SelectOptions,
  type SemanticDescription,
  type SemanticField,
} from '@solum/sdk'
import type { Filter, FilterFieldDef, LookupSection } from '@solum/ui'
import { displayLabel } from '../data/queries'
import { cachedSearch } from '../data/useLookupSearch'
import { buildRpcArgs, rowsToHits, type SearchTool } from '../app/federatedSearch'

interface RawClient {
  select<T = Row>(schema: string, table: string, opts?: SelectOptions): Promise<T[]>
  /** Needed only to reach a SUBJECT target's registered search door (#2444). */
  rpcTable?<T = Row>(fn: string, args?: Row, schema?: string): Promise<T[]>
}

interface SemanticClient {
  describeSemantics(entity?: string): Promise<SemanticDescription>
}

export const ENTITY_LOOKUP_LIMIT = 20
export const ENTITY_LOOKUP_STALE_MS = 30_000

const lookupSection = (items: LookupSection['items'], sourceCount = items.length): LookupSection[] => [{
  items,
  // At exactly the cap the source cannot prove there is no next item. Say so.
  complete: sourceCount < ENTITY_LOOKUP_LIMIT,
}]

const FILTER_KIND: Record<string, NonNullable<FilterFieldDef['type']>> = {
  text: 'text', email: 'text', phone: 'text', country: 'text',
  number: 'number', money: 'number', percent: 'number',
  date: 'date', datetime: 'date', option: 'select',
}

const TEXTUAL = new Set(['text', 'email', 'phone', 'country'])

const options = (field: SemanticField) =>
  ((field.options ?? []) as { value: unknown; label: string; selectable?: boolean }[])
    .filter((option) => option.selectable !== false)
    .map((option) => ({ value: String(option.value), label: option.label }))

/** The governed advanced-search vocabulary for one target entity. */
export function advancedLookupFields(fields: SemanticField[]): FilterFieldDef[] {
  return fields
    .filter((field) => FILTER_KIND[field.kind])
    .sort((a, b) => a.sort - b.sort)
    .map((field) => ({
      name: field.column_name,
      label: field.label,
      type: FILTER_KIND[field.kind],
      ...(field.kind === 'option' ? { options: options(field) } : {}),
    }))
}

/** The first declared text field that can honestly carry a plain search. */
export function lookupSearchField(fields: SemanticField[]): SemanticField | null {
  const textual = fields.filter((field) => TEXTUAL.has(field.kind)).sort((a, b) => a.sort - b.sort)
  return textual.find((field) => field.is_summary) ?? textual[0] ?? null
}

/**
 * A record label from declared summary data. The UUID is deliberately never a
 * fallback: a missing/hidden row and an unknown id must remain indistinguishable.
 */
export function lookupLabel(row: Row, fields: SemanticField[]): string {
  // The projection is the authority: only fields the semantic layer declared
  // as naming data can participate. Over that safe subset, use the console's
  // one entity-neutral record-label contract so people, meetings, products and
  // future modules do not grow picker-specific naming rules.
  const naming = new Set(entityLookupColumns(fields).split(',').slice(1))
  const projected = Object.fromEntries(
    Object.entries(row).filter(([column]) => naming.has(column)),
  ) as Row
  const label = displayLabel(projected).trim()
  return label && label !== 'Record unavailable' ? label : 'Record'
}

export class UnsupportedLookupFilterError extends Error {
  constructor(message = 'This filter cannot be applied to this relationship.') {
    super(message)
    this.name = 'UnsupportedLookupFilterError'
  }
}

export class LookupSearchUnavailableError extends Error {
  constructor() {
    super('This relationship search is unavailable. Try again.')
    this.name = 'LookupSearchUnavailableError'
  }
}

const hasValue = (filter: Filter) => filter.op === 'empty'
  || (Array.isArray(filter.value) ? filter.value.length > 0 : filter.value !== '' && filter.value != null)

/** Translate simple text + advanced leaves all-or-nothing. */
export function entityLookupFilters(
  fields: SemanticField[], query: string, advanced: Filter[],
): NonNullable<SelectOptions['filters']> {
  const complete = advanced.filter(hasValue)
  const searchField = lookupSearchField(fields)
  if (query.trim() && !searchField) {
    throw new UnsupportedLookupFilterError('This relationship has no searchable field.')
  }
  const leaves: FilterLeaf[] = [
    ...(query.trim() && searchField
      ? [{ field: searchField.column_name, op: 'contains', value: query.trim() }]
      : []),
    ...complete,
  ]
  if (leaves.length === 0) return []

  const allowed = new Map(fields.map((field) => [field.column_name, field.kind]))
  if (leaves.some((filter) => !filter.field || !allowed.has(filter.field))) {
    throw new UnsupportedLookupFilterError()
  }
  const translated = filtersToPostgrest(leaves, { kindOf: (field) => allowed.get(field) })
  if (!translated) throw new UnsupportedLookupFilterError()
  return translated
}

/** Only select fields needed to name a hit; never pull the target's whole row. */
export function entityLookupColumns(fields: SemanticField[]): string {
  const ordered = [...fields].sort((a, b) => a.sort - b.sort)
  const summary = ordered.filter((field) => field.is_summary)
  const labels = summary.length ? summary : ordered.filter((field) => TEXTUAL.has(field.kind)).slice(0, 2)
  return ['id', ...new Set(labels.map((field) => field.column_name).filter((name) => name !== 'id'))].join(',')
}

/**
 * The same question for a SUBJECT target (#2444 slice 4), asked only in words
 * the governed door answers faithfully — or refused, saying why.
 *
 *  · words typed into the picker go to the target module's REGISTERED search
 *    door (ranked, server-side, recording only what it returns); a free-text
 *    `contains` is not in the subject door's vocabulary, and the door's
 *    exact `eq` is not what a person typing half a name means;
 *  · advanced filters are translated with `leavesToSubjectFilters` — the
 *    translator that shares its operator predicate with the grid's menu;
 *  · the search door takes words only, so words AND filters together are
 *    refused rather than half-applied.
 */
export function subjectLookupPlan(
  fields: SemanticField[], query: string, advanced: Filter[], tool: SearchTool | null,
): { kind: 'search'; tool: SearchTool } | { kind: 'list'; filters: NonNullable<SelectOptions['filters']> } {
  const complete = advanced.filter(hasValue)
  if (query.trim()) {
    if (complete.length > 0) {
      throw new UnsupportedLookupFilterError(
        'This record type is searched through its governed search, which takes words only — clear the filters to search by name, or clear the words to filter.')
    }
    if (!tool) {
      throw new UnsupportedLookupFilterError(
        'This record type is searched only through its governed search, and none is registered here. Filter by a field instead.')
    }
    return { kind: 'search', tool }
  }
  const allowed = new Map(fields.map((field) => [field.column_name, field.kind]))
  if (complete.some((filter) => !filter.field || !allowed.has(filter.field))) throw new UnsupportedLookupFilterError()
  const translated = leavesToSubjectFilters(complete, { kindOf: (field) => allowed.get(field) })
  if (!translated.ok) {
    throw new UnsupportedLookupFilterError(`This record type's governed read cannot apply ${translated.reason}.`)
  }
  return { kind: 'list', filters: translated.filters }
}

/** Build the exact Lookup search callback for one governed target entity. */
export function makeEntitySearch(
  raw: RawClient,
  semanticClient: SemanticClient,
  schema: SchemaDescription | undefined,
  entity: string,
  cache: QueryClient,
  opts: { searchTools?: readonly SearchTool[] } = {},
): (query: string, facet?: string | null, type?: string | null, filters?: Filter[]) => Promise<LookupSection[]> {
  const table = (schema?.entities ?? []).find((candidate) => candidate.key === entity)
  const search = async (query: string, _facet?: string | null, _type?: string | null, advanced: Filter[] = []): Promise<LookupSection[]> => {
    // Missing and caller-hidden targets take the same non-enumerating path.
    if (!table) return []
    try {
      const semantics = await cache.fetchQuery({
        queryKey: ['semantics', entity],
        queryFn: () => semanticClient.describeSemantics(entity),
        staleTime: ENTITY_LOOKUP_STALE_MS,
      })
      const fields = semantics.fields.filter((field) => field.entity_type === entity)
      if (isSubjectEntity(table)) {
        const plan = subjectLookupPlan(fields, query, advanced,
          opts.searchTools?.find((tool) => tool.entity === entity) ?? null)
        if (plan.kind === 'search') {
          if (!raw.rpcTable) throw new UnsupportedLookupFilterError('This record type is searched only through its governed search.')
          const rows = await raw.rpcTable<Row>(plan.tool.fn, buildRpcArgs(plan.tool, query.trim(), ENTITY_LOOKUP_LIMIT), plan.tool.schema)
          const sourceRows = Array.isArray(rows) ? rows : []
          return lookupSection(rowsToHits(sourceRows).map((hit) => ({ id: hit.id, primary: hit.label })), sourceRows.length)
        }
        const rows = await raw.select<Row>(table.schema_name, table.table_name, {
          columns: entityLookupColumns(fields),
          limit: ENTITY_LOOKUP_LIMIT,
          ...(plan.filters.length ? { filters: plan.filters } : {}),
        })
        return lookupSection(rows.map((row) => ({ id: String(row.id), primary: lookupLabel(row, fields) })))
      }
      const filters = entityLookupFilters(fields, query, advanced)
      const rows = await raw.select<Row>(table.schema_name, table.table_name, {
        columns: entityLookupColumns(fields),
        limit: ENTITY_LOOKUP_LIMIT,
        ...(filters.length ? { filters } : {}),
      })
      return lookupSection(rows.map((row) => ({ id: String(row.id), primary: lookupLabel(row, fields) })))
    } catch (error) {
      if (error instanceof UnsupportedLookupFilterError) throw error
      // The SDK's own refusal is a statement about the question, not an outage:
      // "try again" would be a lie (#2444).
      if (isSubjectReadUnsupported(error)) throw new UnsupportedLookupFilterError(error.message)
      throw new LookupSearchUnavailableError()
    }
  }

  /* The shared wrapper is the RESULT cache. Keeping a second fetchQuery here
     under `relation-lookup` made the policy in @solum/experience test-only:
     production never used its stable key and a record save could not make the
     answer stale. `Lookup` stays provider-agnostic; this host boundary is where
     the same search function joins TanStack Query. */
  const cached = cachedSearch(cache, search, { source: entity })
  return async (query, facet, type, filters) => cached(query, facet, type, filters)
}
